import { SlideMarkerQcSummary } from 'api/markerTypes';
import { CellRuleValue } from 'interfaces/cellRule';
import { QCImpactId } from 'interfaces/qcMarkers';
import { MultiplexPanelCellRules } from 'interfaces/study';

export const OTHER_CELL_TYPE_PATH = 'tissue.cell.other_cell';
export const ARTIFACT_CELL_TYPE_PATH = 'tissue.cell.artifact_cell';

const USED_RULE_VALUES = new Set<number>([
  CellRuleValue.MustBeNegative,
  CellRuleValue.MustBePositive,
  CellRuleValue.OneOfTwoMustBePositive,
]);

export interface CellRuleRevisionWarning {
  id: string;
  message: string;
}

export const cellRuleRevisionWarnings = ({
  revision,
  displayNames = {},
  qcSettings,
  slideSummaries = [],
  panelSlideIds,
  labelImpactById = {},
}: {
  revision?: MultiplexPanelCellRules;
  displayNames?: Record<string, string>;
  qcSettings?: Record<string, string>;
  slideSummaries?: SlideMarkerQcSummary[];
  panelSlideIds?: Set<string>;
  labelImpactById?: Record<string, string>;
}): CellRuleRevisionWarning[] => {
  if (!revision?.rules?.length) return [];
  const warnings: CellRuleRevisionWarning[] = [];
  const targets = new Set(revision.rules.map((rule) => rule.targetCellTypePath).filter(Boolean));
  const missingOther = !targets.has(OTHER_CELL_TYPE_PATH);
  const missingArtifact = !targets.has(ARTIFACT_CELL_TYPE_PATH);
  if (missingOther && missingArtifact) {
    warnings.push({
      id: 'missing-required-cells',
      message: 'This revision does not include other cells or artifact cells.',
    });
  } else if (missingOther) {
    warnings.push({ id: 'missing-other-cells', message: 'This revision does not include other cells.' });
  } else if (missingArtifact) {
    warnings.push({ id: 'missing-artifact-cells', message: 'This revision does not include artifact cells.' });
  }

  const usedMarkerIds: string[] = [];
  revision.rules.forEach((rule) => {
    Object.entries(rule.rules ?? {}).forEach(([markerId, ruleValue]) => {
      if (markerId && USED_RULE_VALUES.has(ruleValue) && !usedMarkerIds.includes(markerId))
        usedMarkerIds.push(markerId);
    });
  });
  const failedMarkerIds = usedMarkerIds.filter((markerId) =>
    markerFailedQc(markerId, qcSettings, slideSummaries, panelSlideIds, labelImpactById)
  );
  if (failedMarkerIds.length > 0) {
    const names = failedMarkerIds.map((markerId) => displayNames[markerId] || markerId);
    warnings.push({
      id: 'failed-qc-markers',
      message: `This revision uses markers that failed QC: ${names.join(', ')}.`,
    });
  }
  return warnings;
};

const markerFailedQc = (
  markerId: string,
  qcSettings: Record<string, string> | undefined,
  slideSummaries: SlideMarkerQcSummary[],
  panelSlideIds: Set<string> | undefined,
  labelImpactById: Record<string, string>
) => {
  if (qcSettings?.[markerId] === QCImpactId.EXCLUDE) return true;
  return slideSummaries.some((slide) => {
    if (panelSlideIds && !panelSlideIds.has(slide.slideId)) return false;
    // channelMarkersQc is keyed by channel index, so find the marker's channels first.
    return Object.entries(slide.channelMarkerTypes ?? {}).some(([channelId, channelMarkerId]) => {
      if (channelMarkerId !== markerId) return false;
      const labelId = slide.channelMarkersQc?.[channelId];
      return Boolean(labelId && labelImpactById[labelId] === QCImpactId.EXCLUDE);
    });
  });
};
